import { Trash } from "lucide-react";
import { FC } from "react";
import { Button } from "./button";
import { Input } from "./input";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { T } from "@/i18n";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type KVObject = Record<string, any>;

interface KvProps {
  id?: string;
  value: KVObject;
  onChange: (obj: KVObject) => void;
  className?: string;
  keyTranslation?: string;
  valueTranslation?: string;
  addTranslation?: string;
  allowDelete?: boolean;
  allowKeyEdit?: boolean;
  allowAdd?: boolean;
}

export const Kv: FC<KvProps> = ({
  id,
  onChange,
  value,
  className,
  addTranslation,
  keyTranslation,
  valueTranslation,
  allowAdd = true,
  allowDelete = true,
  allowKeyEdit = true,
}) => {
  const { t } = useTranslation();
  return (
    <div className={cn("flex flex-col gap-4", className)} id={id}>
      {Object.entries(value).map(([key, val], i) => (
        <div key={i} className="grid gap-4">
          <div className="flex items-center gap-2">
            {allowDelete && (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive"
                onClick={() => {
                  const newValue = { ...value };
                  delete newValue[key];
                  onChange(newValue);
                }}
              >
                <Trash />
              </Button>
            )}
            <Input
              placeholder={t(keyTranslation ?? "common.inputs.kv.key")}
              value={key}
              disabled={!allowKeyEdit}
              onInput={(e) => {
                const newKey = e.currentTarget.value;
                if (newKey in value) {
                  return;
                }
                const newValue = { ...value };
                delete newValue[key];
                newValue[newKey] = val;
                onChange(newValue);
              }}
            />
          </div>
          <div className={cn(allowDelete && "pl-14")}>
            <Input
              placeholder={t(valueTranslation ?? "common.inputs.kv.value")}
              value={val}
              className="w-full"
              onChange={(e) => {
                const newValue = { ...value };
                newValue[key] = e.target.value;
                onChange(newValue);
              }}
            />
          </div>
        </div>
      ))}
      {allowAdd && (
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            onChange({ ...value, "": "" });
          }}
        >
          <T tKey={addTranslation ?? "common.inputs.kv.add"} />
        </Button>
      )}
    </div>
  );
};
